import { useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface FormFileUploadProps {
  label: string;
  bucket: 'avatars' | 'documents' | 'imports';
  /** Current file URL (preview) */
  value: string | null;
  onChange: (url: string | null) => void;
  /** Accept attribute (e.g. "image/*") */
  accept?: string;
  /** Max bytes (default 5 MB) */
  maxBytes?: number;
  className?: string;
  fullWidth?: boolean;
  hint?: string;
  /** Sub-folder inside bucket */
  folder?: string;
}

export function FormFileUpload({
  label,
  bucket,
  value,
  onChange,
  accept = 'image/*',
  maxBytes = 5 * 1024 * 1024,
  className,
  fullWidth,
  hint,
  folder = '',
}: FormFileUploadProps): JSX.Element {
  const [uploading, setUploading] = useState(false);

  const onFile = async (file: File): Promise<void> => {
    if (file.size > maxBytes) {
      toast.error(`Fichier trop volumineux (max ${(maxBytes / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    setUploading(true);
    const ext = file.name.split('.').pop() ?? 'bin';
    const path = `${folder ? folder + '/' : ''}${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });
    setUploading(false);
    if (error) {
      toast.error(`Upload échoué : ${error.message}`);
      return;
    }
    if (bucket === 'avatars') {
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      onChange(data.publicUrl);
    } else {
      onChange(path);
    }
    toast.success('Fichier uploadé');
  };

  return (
    <div className={cn('space-y-1.5', fullWidth && 'sm:col-span-2', className)}>
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {value && (
          <div className="relative h-14 w-14 overflow-hidden rounded-md border border-border bg-muted">
            {accept.includes('image') ? (
              <img src={value} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                Fichier
              </div>
            )}
            <button
              type="button"
              onClick={() => onChange(null)}
              className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5 text-muted-foreground hover:text-destructive"
              aria-label="Retirer le fichier"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-secondary">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {uploading ? 'Upload…' : value ? 'Remplacer' : 'Choisir un fichier'}
          <input
            type="file"
            accept={accept}
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </label>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            Effacer
          </Button>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
